<template>
  <el-dialog
    v-el-drag-dialog
    :visible.sync="innerVisible"
    :title="$t('SeaExport.SelectContainerInfo')"
    width="850px"
    :close-on-click-modal="false"
    class="app-main-inner"
    append-to-body
  >
    <cargo-ag-grid
      config-module="130203"
      :grid-options="gridOptions"
      :column-defs="columnDefs"
      :row-data="rowData"
      style="width:100%;height:300px"
      @gridReady="params=>{gridApi=params.api}"
    />
    <span slot="footer">
      <el-button type="primary" no="1" @click="confirm">{{ $t('CargoMsg.Cargo_ConfirmR') }}</el-button>
      <el-button no="2" @click="innerVisible=false">{{ $t('CargoMsg.Cargo_Cancel') }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import ColumnUtils from '@/utils/ColumnUtils'
import * as CargoDocConsts from '@/class/cargo/model/parameters/CargoDocConsts'
import * as BillType from '@/class/cargo/model/parameters/BillType'
import { SeaExportJob } from '@/class/model/booking/SeaExportJob'
import lodash from 'lodash'
import { SeaCargoDoc } from '@/class/model/booking/SeaCargoDoc'
import { cache, cacheName } from '@/utils/cacheManager'
import * as NumberUtils from '@/utils/NumberUtils'

export default {
  name: 'AddCargoDocContainerDialog',
  props: {
    visible: { type: Boolean, required: true },
    job: { type: Object, required: true },
    docType: { type: String, required: true },
    pageSource: { type: String, required: false, default: '' },
    uuidNotEmpty: { type: Boolean, default: false },
    cargoDocs: { type: Array, default: null }
  },
  data() {
    return {
      innerVisible: false,
      gridOptions: {
        rowSelection: 'multiple',
        suppressCellSelection: true,
        context: {
          componentParent: this
        }
      },
      columnDefs: [
        {
          headerName: '',
          headerCheckboxSelection: true,
          checkboxSelection: true,
          width: 55,
          sortable: false,
          suppressMenu: true,
          suppressSizeToFit: true,
          resizable: false
        }, {
          headerName: this.$t('CargoMsg.Cargo_ContainerSize'),
          field: 'eqSize',
          width: 100,
          cellRenderer: (params) => {
            return params.data.container.eqSize + "'"
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_ContainerType'),
          field: 'eqType',
          width: 100,
          cellRenderer: (params) => {
            return params.data.container.eqType
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_ContainerNo'),
          field: 'containerNo',
          width: 100,
          cellRenderer: (params) => {
            return params.data.container.containerNo
          }
        }, {
          headerName: this.$t('SeaExport.SealNo'),
          field: 'sealNo',
          width: 100,
          cellRenderer: (params) => {
            return params.data.container.sealNo
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_ItemsNBR'),
          field: 'quantity',
          width: 100,
          cellStyle: params => {
            if (params.data.quantityDisabled) {
              return { background: '#f0f3f8', color: '#CCCCCC' }
            }
          }
        }, {
          headerName: this.$t('SeaExport.Weight'),
          field: 'weight',
          width: 100,
          cellStyle: params => {
            if (params.data.weightDisabled) {
              return { background: '#f0f3f8', color: '#CCCCCC' }
            }
          },
          cellRenderer: params => {
            return ColumnUtils.number3ColumnLabelFunc(params.value)
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_Cubage'),
          field: 'cubage',
          width: 100,
          cellStyle: params => {
            if (params.data.cubageDisabled) {
              return { background: '#f0f3f8', color: '#CCCCCC' }
            }
          },
          cellRenderer: params => {
            return ColumnUtils.number3ColumnLabelFunc(params.value)
          }
        }
      ],
      rowData: [],
      gridApi: null
    }
  },
  watch: {
    visible(newVal) {
      this.innerVisible = newVal
      if (newVal) {
        this.toQuery()
      }
    },
    innerVisible(newVal) {
      this.$emit('update:visible', newVal)
      if (!newVal) {
        this.rowData = []
      }
    }
  },
  created() {
    if (this.visible) {
      this.innerVisible = true
      this.toQuery()
    }
  },
  methods: {
    async toQuery() {
      if (!this.job.uuid) {
        this.rowData = []
        return
      }
      const job = new SeaExportJob(this.job)
      let jointSlaves = null
      if (this.docType === CargoDocConsts.MBL2) {
        jointSlaves = await job.getJointSlavesMBL()
      } else {
        jointSlaves = await job.getJointSlavesHBL()
      }
      await job.loadJobRelated({ containerUsages: true })
      let rowData = lodash.cloneDeep(job.containerUsages)
      if ((this.docType === CargoDocConsts.MBL2 && job.typeMBL === BillType.MERGE_BILL_TYPE.code ||
        this.docType === CargoDocConsts.HBL2 && job.typeHBL === BillType.MERGE_BILL_TYPE.code) &&
        jointSlaves && jointSlaves.length > 0) {
        let uuids = ''
        if (jointSlaves.length > 1) {
          for (const job of jointSlaves) {
            if (uuids) {
              uuids = uuids + ',' + job.uuid
            } else {
              uuids = job.uuid
            }
          }
        } else if (jointSlaves.length === 1) {
          uuids = jointSlaves[0].uuid
        }
        rowData = rowData.concat(await SeaExportJob.getListJobContainerUsages(uuids))
      }
      if (this.uuidNotEmpty) {
        rowData = rowData.filter(p => p.uuid)
      }
      if (this.cargoDocs) {
        await cache.load(cacheName.CONFIG)
        if (this.docType === CargoDocConsts.HBL2 && Number(cache.config.get('SplitDocContainerAutoCalculateQWC')) === 1) {
          const quantityMap = new Map()
          const weightMap = new Map()
          const cubageMap = new Map()
          for (const cargoDoc of this.cargoDocs) {
            const ediContainers = await new SeaCargoDoc(cargoDoc).getEdiContainer()
            if (!ediContainers || ediContainers.length === 0) {
              continue
            }
            for (const ediContainer of ediContainers) {
              if (ediContainer.deleted) {
                continue
              }
              const key = `${ediContainer.eqSize}-${ediContainer.eqType}-${ediContainer.containerNo}-${ediContainer.sealNo}`
              const quantity = Number(ediContainer.quantity)
              if (quantity !== 0) {
                quantityMap.set(key, (quantityMap.get(key) || 0) + quantity)
              }
              const weight = Number(ediContainer.weight)
              if (weight !== 0) {
                weightMap.set(key, (weightMap.get(key) || 0) + weight)
              }
              const cubage = Number(ediContainer.cubage)
              if (cubage !== 0) {
                cubageMap.set(key, (cubageMap.get(key) || 0) + cubage)
              }
            }
          }
          for (let i = rowData.length - 1; i >= 0; --i) {
            const row = rowData[i]
            if (!(row.quantity > 0 || row.weight > 0 || row.cubage > 0)) {
              continue
            }
            const key = `${row.eqSize}-${row.eqType}-${row.containerNo}-${row.sealNo}`
            const quantitySum = quantityMap.get(key) || 0
            if (row.quantity !== 0 && quantitySum < row.quantity) {
              row.quantity = NumberUtils.ToFixed2(row.quantity - quantitySum, 2)
            } else {
              row.quantity = 0
              row.quantityDisabled = true
            }
            const weightSum = weightMap.get(key) || 0
            if (row.weight !== 0 && weightSum < row.weight) {
              row.weight = NumberUtils.ToFixed2(row.weight - weightSum, 3)
            } else {
              row.weight = 0
              row.weightDisabled = true
            }
            const cubageSum = cubageMap.get(key) || 0
            if (row.cubage !== 0 && cubageSum < row.cubage) {
              row.cubage = NumberUtils.ToFixed2(row.cubage - cubageSum, 3)
            } else {
              row.cubage = 0
              row.cubageDisabled = true
            }
            if (row.quantityDisabled && row.weightDisabled && row.cubageDisabled) {
              rowData.splice(i, 1)
            }
          }
        } else {
          const quantityMap = new Map()
          const weightMap = new Map()
          const cubageMap = new Map()
          for (const cargoDoc of this.cargoDocs) {
            const ediContainers = await new SeaCargoDoc(cargoDoc).getEdiContainer()
            if (!ediContainers || ediContainers.length === 0) {
              continue
            }
            for (const ediContainer of ediContainers) {
              if (ediContainer.deleted) {
                continue
              }
              let key = ediContainer.containerUsageUuid
              if (this.pageSource === 'subBill') {
                key = `${ediContainer.eqSize}-${ediContainer.eqType}-${ediContainer.containerNo}-${ediContainer.sealNo}`
              }
              const quantity = Number(ediContainer.quantity)
              if (quantity !== 0) {
                quantityMap.set(key, (quantityMap.get(key) || 0) + quantity)
              }
              const weight = Number(ediContainer.weight)
              if (weight !== 0) {
                weightMap.set(key, (weightMap.get(key) || 0) + weight)
              }
              const cubage = Number(ediContainer.cubage)
              if (cubage !== 0) {
                cubageMap.set(key, (cubageMap.get(key) || 0) + cubage)
              }
            }
          }
          for (let i = rowData.length - 1; i >= 0; --i) {
            const row = rowData[i]
            if (!row.uuid || !(row.quantity > 0 || row.weight > 0 || row.cubage > 0)) {
              continue
            }
            let key = row.uuid
            if (this.pageSource === 'subBill') {
              key = `${row.eqSize}-${row.eqType}-${row.containerNo}-${row.sealNo}`
            }
            const quantitySum = quantityMap.get(key) ? NumberUtils.ToFixed2(quantityMap.get(key), 2) : 0.0
            if (row.quantity !== 0 && quantitySum < row.quantity) {
              row.quantity = NumberUtils.ToFixed2(row.quantity - quantitySum, 3)
            } else {
              row.quantity = 0
            }
            const weightSum = weightMap.get(key) ? NumberUtils.ToFixed2(weightMap.get(key), 3) : 0.0
            if (row.weight !== 0 && weightSum < row.weight) {
              row.weight = NumberUtils.ToFixed2(row.weight - weightSum, 3)
            } else {
              row.weight = 0
            }
            const cubageSum = cubageMap.get(key) ? NumberUtils.ToFixed2(cubageMap.get(key), 3) : 0.0
            if (row.cubage !== 0 && cubageSum < row.cubage) {
              row.cubage = NumberUtils.ToFixed2(row.cubage - cubageSum, 3)
            } else {
              row.cubage = 0
            }
            if (row.quantity === 0 && row.weight === 0 && row.cubage === 0) {
              rowData.splice(i, 1)
            }
          }
        }
      }
      this.rowData = rowData
    },
    confirm() {
      const rows = this.gridApi.getSelectedRows()
      this.$emit('callback', rows)
      this.innerVisible = false
    }
  }
}
</script>

<style>
</style>
